import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {NextVisitReminderSettings} from "../models/next-visit-reminder-settings.model";
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {VetClinic} from "../models/vet-clinic.model";

/**
 * Serwis warstwy danych zarządzający konfiguracją i parametrami systemu przypomnień
 * o kolejnych wizytach. Obsługuje personalizację ustawień per placówka
 * oraz pobieranie globalnych szablonów konfiguracyjnych systemu.
 */
@Injectable({
  providedIn: 'root'
})
export class NextVisitReminderSettingsService {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Tworzy nowy profil ustawień przypomnień dla placówki.
   *
   * @param nextVisitReminderSettings Zinstancjonowany obiekt zawierający konfigurację.
   * @returns Observable emitujący utworzony i zapisany obiekt ustawień.
   */
  addNextVisitReminderSettings(nextVisitReminderSettings: NextVisitReminderSettings) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/next-visit-reminder-settings', nextVisitReminderSettings).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje modyfikacje w istniejącym profilu ustawień przypomnień.
   *
   * @param nextVisitReminderSettings Obiekt zawierający zmodyfikowane parametry.
   * @returns Observable emitujący zaktualizowany obiekt ustawień.
   */
  editNextVisitReminderSettings(nextVisitReminderSettings: NextVisitReminderSettings) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/next-visit-reminder-settings', nextVisitReminderSettings).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje główny typ/tryb działania przypomnień dla danej kliniki.
   *
   * @param uuid Unikalny identyfikator kliniki.
   * @param nvrsType Łańcuch znaków określający tryb.
   * @returns Observable emitujący zaktualizowany obiekt nadrzędny kliniki (VetClinic).
   */
  changeNextVisitReminderSettingsType(uuid: string, nvrsType: string) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/next-visit-reminder-settings/type', {'uuid': uuid, 'type':nvrsType}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera szczegóły konfiguracji przypomnień na podstawie identyfikatora profilu ustawień.
   *
   * @param uuid Unikalny identyfikator konfiguracji.
   * @returns Observable emitujący pełen obiekt ustawień.
   */
  getNextVisitReminderSettings(uuid: string): Observable<NextVisitReminderSettings> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/next-visit-reminder-settings/${uuid}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalny, standardowy profil ustawień zdefiniowany w systemie.
   *
   * @returns Observable emitujący domyślny obiekt ustawień.
   */
  getDefaultSettings(): Observable<NextVisitReminderSettings> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/next-visit-reminder-settings-default', {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera alternatywny, globalny profil ustawień cechujący się formalną komunikacją.
   *
   * @returns Observable emitujący formalny, domyślny obiekt ustawień.
   */
  getFormalDefaultSettings(): Observable<NextVisitReminderSettings> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/next-visit-reminder-settings-formal-default', {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera aktualnie aktywny profil ustawień przypomnień przypisany do konkretnej kliniki.
   *
   * @param vetcodeId Unikalny numer identyfikacyjny kliniki.
   * @returns Observable emitujący profil ustawień dla tej placówki.
   */
  getNextVisitReminderSettingsByVetcode(vetcodeId: number) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/next-visit-reminder-settings/by-vetcode/${vetcodeId}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminderSettings(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Wykonuje złożoną aktualizację konfiguracji, zmieniając jednocześnie tryb działania
   * oraz offset czasowy określający widoczność wolnych terminów w kalendarzu.
   *
   * @param uuid Unikalny identyfikator kliniki.
   * @param nvrsType Typ powiadomień.
   * @param daysToMakeReservationTermAvailableForReminders Offset w dniach dla rezerwacji z przypomnień.
   * @returns Observable emitujący zaktualizowany obiekt nadrzędny kliniki (VetClinic).
   */
  changeNextVisitReminderSettings(uuid: string, nvrsType: string, daysToMakeReservationTermAvailableForReminders) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/next-visit-reminder-settings/type', {'uuid': uuid, 'type':nvrsType, 'daysToMakeReservationTermAvailableForReminders':daysToMakeReservationTermAvailableForReminders}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

}
